/**
 * Terminal kit — the visual vocabulary for the "private-wealth terminal"
 * direction. See /DESIGN.md. Dark-first, data-dense, color reserved for
 * meaning. Screens compose these instead of bespoke markup.
 */
import type { HTMLAttributes, ReactNode } from 'react';

import { ConvictionGauge } from '@/components/recommendation/conviction-gauge';
import { LiveNumber } from '@/components/ui/live-number';
import { LAUNCH_LIMITED } from '@/lib/launch-mode';
import { cn } from '@/lib/utils';

type Call = 'BUY' | 'SELL' | 'HOLD';

const CALL_PILL: Record<Call, string> = {
  BUY: 'text-[var(--color-buy)] bg-[color-mix(in_srgb,var(--color-buy)_14%,transparent)] border-[color-mix(in_srgb,var(--color-buy)_36%,transparent)]',
  SELL: 'text-[var(--color-sell)] bg-[color-mix(in_srgb,var(--color-sell)_14%,transparent)] border-[color-mix(in_srgb,var(--color-sell)_36%,transparent)]',
  HOLD: 'text-[var(--color-hold)] bg-[color-mix(in_srgb,var(--color-hold)_14%,transparent)] border-[color-mix(in_srgb,var(--color-hold)_36%,transparent)]',
};

/** BUY / SELL / HOLD pill — tinted fill + bordered, terminal style. */
export function CallPill({ call, className }: { call: Call; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block rounded-[3px] border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em]',
        CALL_PILL[call],
        className,
      )}
    >
      {call}
    </span>
  );
}

/** Thin conviction meter (0-10), colored by call. */
export function ConvictionMeter({
  value,
  call = 'BUY',
  showValue = true,
  className,
}: {
  value: number;
  call?: Call;
  showValue?: boolean;
  className?: string;
}) {
  // Delegate to ConvictionGauge: with Enhanced OFF it renders this exact flat
  // bar (byte-identical baseline); with Enhanced ON it becomes the 10-segment
  // signature gauge. Delegating here cascades the gauge to every direct
  // ConvictionMeter consumer (dashboard, stock page, portfolios, recs detail).
  return <ConvictionGauge value={value} call={call} showValue={showValue} className={className} />;
}

/** Uppercase mono eyebrow label. Amber by default (Bloomberg-terminal signature). */
export function Eyebrow({
  children,
  className,
  tone = 'amber',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'amber' | 'steel' | 'muted';
}) {
  const color =
    tone === 'amber'
      ? 'text-[var(--color-amber)]'
      : tone === 'steel'
        ? 'text-[var(--color-steel)]'
        : 'text-[var(--color-faint)]';
  return (
    <p className={cn('font-mono text-[11px] uppercase tracking-[0.14em]', color, className)}>
      {children}
    </p>
  );
}

/** Serif section heading. */
export function SectionHeading({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={cn('font-serif text-[1.3rem] leading-tight text-[var(--color-text)]', className)}
    >
      {children}
    </h2>
  );
}

/** Hairline-bordered dark panel. */
export function Panel({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'sig-elev rounded-[4px] border border-[var(--color-hair)] bg-[var(--color-surface)]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Consistent panel header — leading icon, serif heading, optional trailing
 * node (count badge / action). Use across every CRM card so chrome matches.
 */
export function PanelHeading({
  icon,
  children,
  trailing,
  className,
}: {
  icon?: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b border-[var(--color-hair)] px-5 py-3',
        className,
      )}
    >
      {icon ? <span className="text-[var(--color-text-muted)]">{icon}</span> : null}
      <SectionHeading className="text-[1rem]">{children}</SectionHeading>
      {trailing != null ? <span className="ml-auto">{trailing}</span> : null}
    </div>
  );
}

/** KPI stat cell — label, big tabular value, optional delta. */
export function Stat({
  label,
  value,
  delta,
  deltaTone = 'muted',
  className,
}: {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  deltaTone?: 'up' | 'down' | 'muted';
  className?: string;
}) {
  const tone =
    deltaTone === 'up'
      ? 'text-[var(--color-buy)]'
      : deltaTone === 'down'
        ? 'text-[var(--color-sell)]'
        : 'text-[var(--color-text-muted)]';
  return (
    <div className={cn('bg-[var(--color-surface)] p-4', className)}>
      <div className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--color-faint)]">
        {label}
      </div>
      <div className="mt-2 text-[1.7rem] font-medium leading-none tabular-nums tracking-tight text-[var(--color-text)]">
        {value}
      </div>
      {delta != null && <div className={cn('mt-1 text-[13px] tabular-nums', tone)}>{delta}</div>}
    </div>
  );
}

/** Row of KPI stats joined by hairlines. */
export function StatRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'grid gap-px overflow-hidden border border-[var(--color-hair)] bg-[var(--color-hair)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Tiny sparkline. up=buy green, down=sell red. */
export function Sparkline({
  points,
  dir = 'up',
  width = 64,
  height = 22,
}: {
  points: number[];
  dir?: 'up' | 'down';
  width?: number;
  height?: number;
}) {
  if (!points.length) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = width / (points.length - 1 || 1);
  const d = points
    .map((p, i) => `${(i * step).toFixed(1)},${(height - ((p - min) / span) * height).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline
        fill="none"
        strokeWidth="1.5"
        stroke={dir === 'up' ? 'var(--color-buy)' : 'var(--color-sell)'}
        points={d}
      />
    </svg>
  );
}

/** Ticker in mono with faint exchange suffix. */
export function Ticker({
  symbol,
  exchange,
  className,
}: {
  symbol: string;
  exchange?: string;
  className?: string;
}) {
  return (
    <span className={cn('font-mono font-medium text-[var(--color-text)]', className)}>
      {symbol}
      {exchange && (
        <span className="ml-1.5 text-[0.68em] font-normal text-[var(--color-faint)]">
          {exchange}
        </span>
      )}
    </span>
  );
}

// ============================================================================
// Bloomberg-terminal chrome
// ============================================================================

export interface TapeItem {
  label: string;
  price?: number | null;
  changePct?: number | null;
}

/**
 * Scrolling ticker tape (Bloomberg signature). Honest: shows "—" when a
 * price is unavailable, never fabricates movement. Pauses on hover and
 * respects prefers-reduced-motion (falls back to a static, wrapped row).
 */
export function TickerTape({
  items,
  className,
  realtime = false,
}: {
  items: TapeItem[];
  className?: string;
  /** True only when the caller KNOWS every price is from a real-time feed. */
  realtime?: boolean;
}) {
  if (!items.length) return null;
  /**
   * `track` distinguishes the two copies of the marquee content.
   *
   * Both copies render the same items, so without a per-copy prefix they emit
   * IDENTICAL keys. Combined with the asymmetric child shape the previous build
   * used — a bare array next to an element — React could align a cell from one
   * copy against the wrapper of the other, which is exactly the hydration
   * mismatch this component was throwing: the client showed an extra
   * `motion-reduce:hidden` wrapper where the server had a cell.
   */
  const cell = (track: string, it: TapeItem, i: number) => {
    const up = (it.changePct ?? 0) >= 0;
    const tone =
      it.changePct == null
        ? 'text-[var(--color-faint)]'
        : up
          ? 'text-[var(--color-buy)]'
          : 'text-[var(--color-sell)]';
    return (
      <span
        key={`${track}-${it.label}-${i}`}
        className="inline-flex items-center gap-2 px-3 sm:px-4"
      >
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-amber)]">
          {it.label}
        </span>
        {/* Values change in place (stable keys, same marquee element), so a
            refresh never restarts the scroll; LiveNumber tints a moved figure
            briefly green or red, and the % colour eases between up and down. */}
        <LiveNumber
          always
          className="text-[11px] text-[var(--color-text)]"
          value={
            it.price != null ? it.price.toLocaleString('en-AU', { minimumFractionDigits: 2 }) : '—'
          }
        />
        <LiveNumber
          always
          className={cn('text-[11px] transition-colors duration-700', tone)}
          value={it.changePct == null ? '' : `${up ? '+' : ''}${it.changePct.toFixed(2)}%`}
        />
        <span className="text-[var(--color-hair-2)]">·</span>
      </span>
    );
  };
  return (
    <div
      className={cn(
        // max-w-full + overflow-hidden guarantee the full-bleed marquee can never
        // force horizontal page overflow on a narrow phone viewport.
        'group flex max-w-full items-stretch overflow-hidden border-y border-[var(--color-hair)] bg-[var(--color-surface)]',
        className,
      )}
    >
      {/* Persistent honest freshness marker, pinned left so it stays visible as
          the marquee scrolls underneath it. Delayed unless the caller knows the
          feed is real-time (US on Twelve Data). Tighter padding on mobile so it
          leaves more room for the figures themselves. */}
      <span
        title={
          realtime
            ? 'Quotes on this tape are taken from a live feed and updated about every minute — not tick-by-tick.'
            : 'All quotes on the tape are delayed or last-known — not a live feed.'
        }
        className="z-[1] flex shrink-0 items-center gap-1.5 border-r border-[var(--color-hair)] bg-[var(--color-surface)] px-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--color-faint)] sm:px-3"
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${realtime ? 'bg-[var(--color-steel)]' : 'bg-[var(--color-amber)]'}`}
          aria-hidden
        />
        {realtime ? 'Every minute' : 'Delayed'}
      </span>
      <div className="flex min-w-0 flex-1 items-center overflow-hidden">
        <div className="flex whitespace-nowrap py-1.5 motion-safe:animate-[sig-marquee_45s_linear_infinite] group-hover:[animation-play-state:paused] motion-reduce:flex-wrap motion-reduce:whitespace-normal">
          {/* Both tracks are wrapped elements, deliberately SYMMETRIC. The
              previous shape was `[array, <span>]` — a bare array beside an
              element — which gives React two children of different kinds to
              reconcile and is what let the copies drift out of alignment during
              hydration. Two wrappers with distinct keys cannot. */}
          <span key="track-a" className="inline-flex items-center">
            {items.map((it, i) => cell('a', it, i))}
          </span>
          {/* The second copy is what makes the loop seamless; it is duplicate
              content, so it is hidden from assistive tech and dropped entirely
              when the marquee is not animating. */}
          <span key="track-b" aria-hidden className="inline-flex items-center motion-reduce:hidden">
            {items.map((it, i) => cell('b', it, i))}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Command bar — the iconic terminal command line. Type "/" to focus.
 * Cosmetic affordance for the demo (no real command routing yet); the
 * function hints evoke the Bloomberg <GO> convention.
 *
 * In launch (charts_crm) mode the defaults drop RECS and TAX — both point at
 * hidden routes — so the omnipresent command bar never advertises a surface the
 * client cannot reach. Callers passing explicit placeholder/hints keep theirs.
 */
const COMMAND_BAR_PLACEHOLDER = LAUNCH_LIMITED
  ? 'Type a ticker or command…  e.g.  CBA AU  ·  WATCH  ·  HELP'
  : 'Type a ticker or command…  e.g.  CBA AU  ·  RECS  ·  HELP';
const COMMAND_BAR_HINTS = LAUNCH_LIMITED
  ? ['WATCH', 'ALERTS', 'HELP']
  : ['RECS', 'WATCH', 'TAX', 'HELP'];

export function CommandBar({
  placeholder = COMMAND_BAR_PLACEHOLDER,
  hints = COMMAND_BAR_HINTS,
  className,
}: {
  placeholder?: string;
  hints?: string[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 border border-[var(--color-hair)] bg-[var(--color-surface)] px-2.5 py-2 sm:gap-3 sm:px-3',
        className,
      )}
    >
      <span className="shrink-0 font-mono text-[12px] font-semibold text-[var(--color-amber)]">
        {'>'}
      </span>
      <input
        type="text"
        placeholder={placeholder}
        // min-w-0 lets the input shrink below its content width so the long
        // placeholder truncates (clips) on a narrow phone instead of forcing
        // overflow; the hint chips below are md:flex only, so mobile gets the
        // full input width.
        className="min-w-0 flex-1 truncate bg-transparent font-mono text-[12px] text-[var(--color-text)] placeholder:text-[var(--color-faint)] focus:outline-none"
      />
      <div className="hidden items-center gap-1.5 md:flex">
        {hints.map((h) => (
          <span
            key={h}
            className="rounded-[2px] border border-[var(--color-hair-2)] px-1.5 py-0.5 font-mono text-[10px] tracking-[0.06em] text-[var(--color-text-muted)]"
          >
            {h}
          </span>
        ))}
      </div>
    </div>
  );
}
